import { FRIST_KATEGORIEN, FristKategorie, SifaFrist } from '../../services/sifa/betreuungTypes';
import type { NeueFrist } from '../../services/sifa/sifaBetreuungService';
import type { FristAmpel } from '../../services/sifa/fristenLogic';

/**
 * Die Regeln der Fristen-Oberflaeche (Faelligkeiten & Vorsorge) — rein,
 * ohne DOM. Die Ampel selbst (fristAmpel) und das Weiterrechnen der
 * naechsten Faelligkeit liegen in services/sifa/fristenLogic.ts und kommen
 * wie bei mitarbeiterModulLogic als Funktion herein; hier steht nur, wie
 * gefiltert, gesucht, beschriftet und der Dialog-Entwurf geprueft wird.
 */

export type { FristAmpel };

/** Die Ampel einer Frist, vom Aufrufer an `fristAmpel(frist, heute)` gebunden. */
export type AmpelFn = (frist: SifaFrist) => FristAmpel;

/** Stufen der Fristen-Ampel: rot ueberfaellig, gelb < 60 Tage, gruen, erledigt. */
export const AMPEL_FRIST: Record<FristAmpel, { label: string; klasse: string; punkt: string }> = {
  rot: { label: 'überfällig', klasse: 'text-red-700 bg-red-50 border-red-200', punkt: 'bg-red-500' },
  gelb: { label: 'bald fällig', klasse: 'text-amber-700 bg-amber-50 border-amber-200', punkt: 'bg-amber-500' },
  gruen: { label: 'im Plan', klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200', punkt: 'bg-emerald-500' },
  erledigt: { label: 'erledigt', klasse: 'text-slate-500 bg-slate-50 border-slate-200', punkt: 'bg-slate-300' },
};

/** Sortierschluessel der Ampel: rot zuerst. */
export const AMPEL_FRIST_RANG: Record<FristAmpel, number> = { rot: 0, gelb: 1, gruen: 2, erledigt: 3 };

export interface FristenFilter {
  kategorie: FristKategorie | 'alle';
  erledigteAnzeigen: boolean;
  suche: string;
}

export const FILTER_VORGABE: FristenFilter = { kategorie: 'alle', erledigteAnzeigen: false, suche: '' };

const KATEGORIE_LABEL: Record<string, string> = Object.fromEntries(FRIST_KATEGORIEN.map((k) => [k.id, k.label]));

/** Beschriftung einer Kategorie; unbekannte Werte (Altdaten) bleiben lesbar. */
export const kategorieLabel = (k: FristKategorie | string): string => KATEGORIE_LABEL[k] || k;

/** Vorschlag fuer das Wiederholungsintervall beim Wechsel der Kategorie; null = einmalig. */
export const intervallVorschlag = (k: FristKategorie): number | null =>
  FRIST_KATEGORIEN.find((x) => x.id === k)?.intervallMonate ?? null;

/**
 * Filter + Volltextsuche. Erledigte Zeilen sind Historie und bleiben ohne
 * „Erledigte anzeigen" draussen; die Suche geht ueber Titel, Zustaendigen,
 * Notiz und Kategorie-Label — alle Woerter muessen vorkommen.
 */
export const filtereFristen = (fristen: SifaFrist[], filter: FristenFilter): SifaFrist[] => {
  const woerter = (filter.suche || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  return fristen.filter((f) => {
    if (!filter.erledigteAnzeigen && f.erledigtAm) return false;
    if (filter.kategorie !== 'alle' && f.kategorie !== filter.kategorie) return false;
    if (woerter.length === 0) return true;
    const text = `${f.titel} ${f.zustaendig || ''} ${f.notiz || ''} ${kategorieLabel(f.kategorie)}`.toLowerCase();
    return woerter.every((w) => text.includes(w));
  });
};

/** Offene Fristen nach Dringlichkeit (Ampel), innerhalb der Stufe nach Faelligkeit. */
export const sortiereNachAmpel = (fristen: SifaFrist[], ampel: AmpelFn): SifaFrist[] =>
  [...fristen].sort((a, b) =>
    AMPEL_FRIST_RANG[ampel(a)] - AMPEL_FRIST_RANG[ampel(b)]
    || a.faelligAm.localeCompare(b.faelligAm));

/** Zaehler fuer die Kopfzeile: offen gesamt und davon ueberfaellig. */
export const zaehleFristen = (fristen: SifaFrist[], ampel: AmpelFn): { offen: number; ueberfaellig: number; erledigt: number } => {
  let offen = 0; let ueberfaellig = 0; let erledigt = 0;
  for (const f of fristen) {
    if (f.erledigtAm) { erledigt += 1; continue; }
    offen += 1;
    if (ampel(f) === 'rot') ueberfaellig += 1;
  }
  return { offen, ueberfaellig, erledigt };
};

/** „alle 12 Monate" / „einmalig". */
export const wiederholungText = (monate: number | null | undefined): string => {
  if (!monate || monate <= 0) return 'einmalig';
  if (monate % 12 === 0) return monate === 12 ? 'jährlich' : `alle ${monate / 12} Jahre`;
  return `alle ${monate} Monate`;
};

// ─── Dialog-Entwurf ─────────────────────────────────────────────────────────

export interface FristEntwurf {
  kategorie: FristKategorie;
  titel: string;
  /** ISO-Datum. */
  faelligAm: string;
  /** Als Text, weil das Feld leer sein darf („einmalig"). */
  intervallMonate: string;
  zustaendig: string;
  notiz: string;
  mitarbeiterId: string;
}

export const leererFristEntwurf = (heuteIso: string): FristEntwurf => ({
  kategorie: 'pruefung',
  titel: '',
  faelligAm: heuteIso,
  intervallMonate: String(intervallVorschlag('pruefung') ?? ''),
  zustaendig: '',
  notiz: '',
  mitarbeiterId: '',
});

export const entwurfAusFrist = (f: SifaFrist): FristEntwurf => ({
  kategorie: f.kategorie,
  titel: f.titel,
  faelligAm: f.faelligAm,
  intervallMonate: f.intervallMonate ? String(f.intervallMonate) : '',
  zustaendig: f.zustaendig || '',
  notiz: f.notiz || '',
  mitarbeiterId: f.mitarbeiterId || '',
});

/** Vorschlag (z. B. ASA) in den Dialog uebernehmen; fehlende Felder bleiben leer. */
export const entwurfAusVorschlag = (v: NeueFrist, heuteIso: string): FristEntwurf => ({
  ...leererFristEntwurf(heuteIso),
  kategorie: v.kategorie,
  titel: v.titel,
  faelligAm: v.faelligAm || heuteIso,
  intervallMonate: v.intervallMonate ? String(v.intervallMonate) : '',
  zustaendig: v.zustaendig || '',
  notiz: v.notiz || '',
  mitarbeiterId: v.mitarbeiterId || '',
});

/** Intervall-Text → ganze Monate; leer/0 = einmalig (null); Unsinn = undefined. */
export const parseIntervall = (text: string): number | null | undefined => {
  const s = (text || '').trim().replace(',', '.');
  if (!s) return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  if (n === 0) return null;
  if (n > 600) return undefined;
  return n;
};

/** Fehlerliste; leer = speicherbar. */
export const pruefeFristEntwurf = (e: FristEntwurf): string[] => {
  const fehler: string[] = [];
  if (!e.titel.trim()) fehler.push('Ein Titel ist Pflicht.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.faelligAm)) fehler.push('„Fällig am" braucht ein Datum.');
  if (parseIntervall(e.intervallMonate) === undefined) fehler.push('Wiederholung: ganze Monate eingeben oder leer lassen.');
  return fehler;
};

export const entwurfZuNeueFrist = (e: FristEntwurf): NeueFrist => ({
  kategorie: e.kategorie,
  titel: e.titel.trim(),
  faelligAm: e.faelligAm,
  intervallMonate: parseIntervall(e.intervallMonate) ?? null,
  zustaendig: e.zustaendig.trim() || null,
  notiz: e.notiz.trim() || null,
  mitarbeiterId: e.mitarbeiterId || null,
});

/** ASA-Hinweis beim ersten Oeffnen: nur ohne jede Frist und bei mehr als 20 Beschaeftigten (§ 11 ASiG). */
export const zeigeAsaHinweis = (fristen: SifaFrist[], beschaeftigte: number | null | undefined, laden: boolean): boolean =>
  !laden && fristen.length === 0 && typeof beschaeftigte === 'number' && beschaeftigte > 20;
